/**
 * Detached, app-shell-owned attachment uploads (#522).
 *
 * Generalises the chore-only mechanism from #518: save the parent record
 * first, upload afterwards without blocking navigation, never fail silently,
 * and warn before the tab closes while files are still in flight.
 *
 * Mechanism only. Which view to refresh, and the sentence the toast is built
 * into, are policy and stay in App.tsx, per the repo's rule that App.tsx is the
 * shell/orchestration layer.
 *
 * The fragments this file DOES produce — why a file failed, and the one-line
 * report naming the files — are display copy composed below every component: a
 * detached run outlives the screen that started it, so there is no `t` in
 * scope. They resolve through `copy` (app/i18n.ts), the same singleton read the
 * API layer and the camera library use, which is exactly the "background queues
 * and plain helpers" case that helper documents (#1088). Only `common` is
 * consulted, and it is eagerly initialised, so a queue that settles seconds
 * after a language change is still in the reader's language.
 */
import { copy } from "../app/i18n.js";
import { ApiError, isTransientNetworkError } from "./api.js";
import { runWithConcurrency, type UploadOutcome } from "./uploadQueue.js";

export type BackgroundUploadResult<T> = {
  /** Index-aligned with the request's files; null where that file failed. */
  results: Array<T | null>;
  uploadedCount: number;
  /** Names of the files that failed, in input order. */
  failedNames: string[];
  /** Why they failed, as a short user-safe phrase in the reader's language
   * ("" when nothing failed) — already resolved, so a caller drops it into a
   * toast rather than translating it again. The report used to be filenames
   * only, so a phone that lost its connection mid-save produced a toast with
   * nothing to act on (#982). */
  failureReason: string;
  /** True when the run was cancelled — report nothing to the user. */
  cancelled: boolean;
  /** Whatever `finalize` threw, if it did. The files still landed; the work
   * that places them (photo order, folder assignment) did not, so a caller
   * that claims success without checking this is reporting a lie (#733). */
  finalizeError?: unknown;
};

export type BackgroundUploadRequest<T> = {
  /** Cancellation handle, e.g. `chore:<taskId>`. Starting the same key twice runs both. */
  key: string;
  files: File[];
  upload: (file: File, index: number) => Promise<T>;
  /** Runs once after every file settles, before onSettled, when anything uploaded. */
  finalize?: (result: BackgroundUploadResult<T>) => Promise<void>;
  onSettled: (result: BackgroundUploadResult<T>) => void;
};

export type BackgroundUploadQueue = {
  start: <T>(request: BackgroundUploadRequest<T>) => void;
  /** Stop every run currently in flight for this key; each reports cancelled and
   * stays silent. Runs started AFTER this call are unaffected, so an Undo cannot
   * silently kill a later upload that reused the key. */
  cancel: (key: string) => void;
  pendingFileCount: () => number;
  subscribe: (listener: (count: number) => void) => () => void;
};

/**
 * A 404 mid-run means the parent was deleted while its files were uploading —
 * the Undo affordances on chore and thing creation do exactly this. That is a
 * cancellation, not a failure the user should be nagged about.
 */
function isParentGone(reason: unknown): boolean {
  return reason instanceof ApiError && reason.status === 404;
}

/**
 * A short, user-safe explanation of why one file failed (#982).
 *
 * Detached uploads are reported through a toast the user cannot expand, so the
 * raw rejection — a browser TypeError string, a server stack, an HTML error
 * page — must never reach it. Each transport failure is mapped onto a phrase
 * the user can act on; anything unrecognised stays deliberately vague.
 *
 * The set is CLOSED — nine phrases over the HTTP statuses this app can produce —
 * so it is a map from the status to a key, not a humanized message. The status
 * itself is never translated: it is a number the user reads back to us.
 */
export function uploadFailureReason(reason: unknown): string {
  if (reason instanceof ApiError) {
    // status 0 is lib/api.ts's normalized "never reached the server".
    if (reason.status === 0) return copy("uploads.reason.noConnection", "no connection");
    if (reason.status === 401) return copy("uploads.reason.sessionExpired", "your session expired");
    if (reason.status === 403) return copy("uploads.reason.permissionDenied", "permission denied");
    if (reason.status === 404) return copy("uploads.reason.gone", "it no longer exists");
    if (reason.status === 413) return copy("uploads.reason.tooLarge", "the file is too large");
    if (reason.status === 415) return copy("uploads.reason.unsupportedType", "that file type isn't supported");
    if (reason.status === 429) return copy("uploads.reason.tooManyRequests", "too many requests — try again shortly");
    if (reason.status >= 500) {
      return copy("uploads.reason.serverProblem", `the server had a problem (HTTP ${reason.status})`, { status: reason.status });
    }
    return copy("uploads.reason.serverRejected", `the server rejected it (HTTP ${reason.status})`, { status: reason.status });
  }
  // Deliberately the same key as status 0: both mean "the request never
  // arrived", and summarizeFailureReasons dedupes on the rendered phrase, so
  // two wordings for one cause would list the same problem twice.
  if (isTransientNetworkError(reason)) return copy("uploads.reason.noConnection", "no connection");
  return copy("uploads.reason.unexpected", "an unexpected error");
}

/** One phrase for a whole batch. Distinct causes are listed (capped, so the
 * toast stays readable at 360px like the filename list above it). */
function summarizeFailureReasons<T>(outcomes: Array<UploadOutcome<T>>): string {
  const reasons = outcomes
    .filter((outcome) => outcome.status === "rejected" && !(outcome.reason instanceof BackgroundUploadCancelled))
    .map((outcome) => uploadFailureReason((outcome as { reason: unknown }).reason));
  const unique = [...new Set(reasons)];
  if (!unique.length) return "";
  // "; " is list punctuation, identical in both languages, so only the tail
  // needs a key.
  if (unique.length <= 2) return unique.join("; ");
  const listed = unique.slice(0, 2).join("; ");
  return copy("uploads.reasonsAndMore", `${listed}; and more`, { reasons: listed });
}

export function createBackgroundUploadQueue(): BackgroundUploadQueue {
  const listeners = new Set<(count: number) => void>();
  // Per RUN, not per key. Cancellation used to live in a shared key set that
  // each run deleted in its own `finally`, so with two runs in flight for the
  // same key the first to finish cleared the flag and the second resumed — an
  // Undo could not reliably stop the uploads it was meant to stop (#733).
  const activeRuns = new Map<string, Set<{ cancelled: boolean }>>();
  // Finalizers for one key run one at a time. They read state the previous run
  // is about to change — the asset's current photos, the project's file entries
  // — so two overlapping runs that both read before either wrote would each
  // persist a view that omits the other's work, and the last writer would undo
  // the first (#733 review). Serializing them makes the second read the first's
  // result, which is the only ordering that converges.
  const finalizeChains = new Map<string, Promise<unknown>>();
  let pending = 0;

  function emit(): void {
    for (const listener of listeners) listener(pending);
  }

  function start<T>(request: BackgroundUploadRequest<T>): void {
    const { key, files, upload, finalize, onSettled } = request;
    if (!files.length) return;

    const run = { cancelled: false };
    const runsForKey = activeRuns.get(key) ?? new Set<{ cancelled: boolean }>();
    runsForKey.add(run);
    activeRuns.set(key, runsForKey);

    pending += files.length;
    emit();

    void (async () => {
      let settledCount = 0;
      try {
        const outcomes = await runWithConcurrency<T>(
          files,
          (file, index) => {
            // Skip the rest of the batch once the parent is gone.
            if (run.cancelled) return Promise.reject(new BackgroundUploadCancelled());
            return upload(file, index);
          },
          {
            onSettled: () => {
              settledCount += 1;
              pending = Math.max(0, pending - 1);
              emit();
            }
          }
        );

        const cancelled = run.cancelled
          || outcomes.some((outcome) => outcome.status === "rejected" && isParentGone(outcome.reason));

        const results = outcomes.map((outcome) => (outcome.status === "fulfilled" ? outcome.value : null));
        const result: BackgroundUploadResult<T> = {
          results,
          uploadedCount: results.filter((entry) => entry != null).length,
          failedNames: cancelled ? [] : rejectedNames(outcomes),
          failureReason: cancelled ? "" : summarizeFailureReasons(outcomes),
          cancelled
        };

        if (!cancelled && result.uploadedCount > 0 && finalize) {
          const previous = finalizeChains.get(key) ?? Promise.resolve();
          const chained = previous.then(() => finalize(result)).catch((error: unknown) => {
            // A failed finalize (reorder, folder assignment) must not lose the
            // upload report — the files did land. It is handed to onSettled
            // rather than swallowed, so the caller can say what did not happen.
            result.finalizeError = error;
          });
          finalizeChains.set(key, chained);
          await chained;
          if (finalizeChains.get(key) === chained) finalizeChains.delete(key);
        }
        // Re-read cancellation: Undo can land WHILE finalize is in flight, and
        // reporting the snapshot taken before it would let a deleted parent
        // still raise a finalize-error toast and publish its ids to the caller.
        if (run.cancelled) {
          onSettled({ ...result, cancelled: true, failedNames: [], failureReason: "", finalizeError: undefined });
          return;
        }
        onSettled(result);
      } finally {
        // Guard against an unexpected throw leaving the counter high forever.
        if (settledCount < files.length) {
          pending = Math.max(0, pending - (files.length - settledCount));
          emit();
        }
        const remaining = activeRuns.get(key);
        remaining?.delete(run);
        if (remaining && remaining.size === 0) activeRuns.delete(key);
      }
    })();
  }

  return {
    start,
    cancel(key: string) {
      for (const run of activeRuns.get(key) ?? []) run.cancelled = true;
    },
    pendingFileCount: () => pending,
    subscribe(listener) {
      listeners.add(listener);
      listener(pending);
      return () => {
        listeners.delete(listener);
      };
    }
  };
}

/** Internal control flow, never rendered: a cancelled run reports nothing, and
 * both readers of this type check `instanceof` rather than the message. */
class BackgroundUploadCancelled extends Error {
  constructor() {
    super("Upload cancelled");
    this.name = "BackgroundUploadCancelled";
  }
}

function rejectedNames<T>(outcomes: Array<UploadOutcome<T>>): string[] {
  return outcomes
    .filter((outcome) => outcome.status === "rejected" && !(outcome.reason instanceof BackgroundUploadCancelled))
    .map((outcome) => outcome.file.name);
}

/** Names at most three files, so the toast stays readable at 360px. The reason
 * (from `failureReason`) is appended when known, so the user learns WHY and not
 * just which files (#982).
 *
 * `label` and the filenames are the household's own text — an asset's name, the
 * file it picked — and pass through verbatim; only the sentence around them
 * changes language. The one/many key is chosen here rather than by i18next's
 * plural rules, because `copy` hands its values to `replace`: a value named
 * `count` reaching the options object would change which key resolves. */
export function backgroundUploadFailureMessage(label: string, failedNames: string[], failureReason = ""): string {
  const shown = failedNames.slice(0, 3).join(", ");
  const rest = failedNames.length - 3;
  const names = rest > 0 ? copy("uploads.namesTruncated", `${shown} +${rest} more`, { shown, rest }) : shown;
  const count = failedNames.length;
  const noun = count === 1 ? "file" : "files";
  const base = copy(
    count === 1 ? "uploads.failedOne" : "uploads.failedMany",
    `Couldn't upload ${count} ${noun} for ${label}: ${names}`,
    { count, label, names }
  );
  return failureReason ? copy("uploads.failedWithReason", `${base} (${failureReason})`, { message: base, reason: failureReason }) : base;
}

/** The app-wide queue. Tests build their own with createBackgroundUploadQueue. */
export const backgroundUploads = createBackgroundUploadQueue();
